<template>
  <!-- D-237 (A4): a real dialog. `aria-modal` scopes AT to this element's own DOCUMENT,
       and here that document contains only the widget — so it cannot wrongly hide host
       page content, which is the usual objection to it.
       🪤 `aria-label`, NOT `aria-labelledby`: the two headings inside are
       `div.share-header` ("Share", "Actions"), not headings and not a title for the
       dialog as a whole. Pointing at one of them would name this "Share" and hide the
       Actions half from the name.
       `tabindex="-1"` is the 0-focusable fallback target, not a tab stop — see
       `focusTrap.js`. -->
  <div class="menu-wrap" ref="dialog" role="dialog" aria-modal="true" aria-label="Episode options" tabindex="-1">
    <button class="close-button" @click="close">
      <span class="is-sr-only">Close menu</span>
      <svg aria-hidden="true" focusable="false"
           stroke="white" fill="white" stroke-width="0"
           viewBox="0 0 24 24"
           height="20px"
           width="20px"
           xmlns="http://www.w3.org/2000/svg"><path d="M6.2253 4.81108C5.83477 4.42056 5.20161 4.42056 4.81108 4.81108C4.42056 5.20161 4.42056 5.83477 4.81108 6.2253L10.5858 12L4.81114 17.7747C4.42062 18.1652 4.42062 18.7984 4.81114 19.1889C5.20167 19.5794 5.83483 19.5794 6.22535 19.1889L12 13.4142L17.7747 19.1889C18.1652 19.5794 18.7984 19.5794 19.1889 19.1889C19.5794 18.7984 19.5794 18.1652 19.1889 17.7747L13.4142 12L19.189 6.2253C19.5795 5.83477 19.5795 5.20161 19.189 4.81108C18.7985 4.42056 18.1653 4.42056 17.7748 4.81108L12 10.5858L6.2253 4.81108Z" fill="white"></path></svg>
    </button>
    <div class="menu-content">
      <div class="section">
        <div class="share-header">Share</div>
        <div class="share-body">
          <!-- D-235 (A3): `aria-label` PINS the accessible name. The visible text still
               flips to "Link copied ✓" for two seconds, but that used to be the button's
               NAME — so AT re-announced the control as a differently-named control, on a
               focused element, where a name change is announced by nobody anyway. The
               confirmation goes through the status region instead; the name stays put. -->
          <button class="share-button" @click="copyLink" aria-label="Copy link">
            <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 448 512" height="14px" width="14px" xmlns="http://www.w3.org/2000/svg"><path d="M384 336H192c-8.8 0-16-7.2-16-16V64c0-8.8 7.2-16 16-16l140.1 0L400 115.9V320c0 8.8-7.2 16-16 16zM192 384H384c35.3 0 64-28.7 64-64V115.9c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1H192c-35.3 0-64 28.7-64 64V320c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64V448c0 35.3 28.7 64 64 64H256c35.3 0 64-28.7 64-64V416H272v32c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V192c0-8.8 7.2-16 16-16H96V128H64z"></path></svg>
            <span style="width: 135px;">
            {{ linkCopied ? 'Link copied ✓' : 'Copy link' }}
            </span>
          </button>
          <a class="share-button" :href="computedShareLink('email')">
            <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 24 24" height="14px" width="14px" xmlns="http://www.w3.org/2000/svg"><path fill="none" d="M0 0h24v24H0V0z"></path><path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6zm-2 0-8 5-8-5h16zm0 12H4V8l8 5 8-5v10z"></path></svg>
              Email
          </a>
          <a class="share-button" :href="computedShareLink('meta')" target="_blank">
            <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 640 512" height="14px" width="14px" xmlns="http://www.w3.org/2000/svg"><path d="M640 317.9C640 409.2 600.6 466.4 529.7 466.4C467.1 466.4 433.9 431.8 372.8 329.8L341.4 277.2C333.1 264.7 326.9 253 320.2 242.2C300.1 276 273.1 325.2 273.1 325.2C206.1 441.8 168.5 466.4 116.2 466.4C43.42 466.4 0 409.1 0 320.5C0 177.5 79.78 42.4 183.9 42.4C234.1 42.4 277.7 67.08 328.7 131.9C365.8 81.8 406.8 42.4 459.3 42.4C558.4 42.4 640 168.1 640 317.9H640zM287.4 192.2C244.5 130.1 216.5 111.7 183 111.7C121.1 111.7 69.22 217.8 69.22 321.7C69.22 370.2 87.7 397.4 118.8 397.4C149 397.4 167.8 378.4 222 293.6C222 293.6 246.7 254.5 287.4 192.2V192.2zM531.2 397.4C563.4 397.4 578.1 369.9 578.1 322.5C578.1 198.3 523.8 97.08 454.9 97.08C421.7 97.08 393.8 123 360 175.1C369.4 188.9 379.1 204.1 389.3 220.5L426.8 282.9C485.5 377 500.3 397.4 531.2 397.4L531.2 397.4z"></path></svg>
            <span>Meta</span>
          </a>
          <a class="share-button"  :href="computedShareLink('x')" target="_blank">
            <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 512 512" height="14px" width="14px" xmlns="http://www.w3.org/2000/svg"><path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z"></path></svg>
            <span>X</span>
          </a>
        </div>
      </div>
      <div class="section">
        <div class="share-header">Actions</div>
        <div class="share-body">
          <button class="share-button" @click="download">
            <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 24 24" height="14px" width="14px" xmlns="http://www.w3.org/2000/svg"><path d="M4 19H20V12H22V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V12H4V19ZM14 9H19L12 16L5 9H10V3H14V9Z"></path></svg>
            <span>Download</span>
          </button>
          <button class="share-button" @click="subscribeRSS">
            <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 448 512" height="14px" width="14px" xmlns="http://www.w3.org/2000/svg"><path d="M128.081 415.959c0 35.369-28.672 64.041-64.041 64.041S0 451.328 0 415.959s28.672-64.041 64.041-64.041 64.04 28.673 64.04 64.041zm175.66 47.25c-8.354-154.6-132.185-278.587-286.95-286.95C7.656 175.765 0 183.105 0 192.253v48.069c0 8.415 6.49 15.472 14.887 16.018 111.832 7.284 201.473 96.702 208.772 208.772.547 8.397 7.604 14.887 16.018 14.887h48.069c9.149.001 16.489-7.655 15.995-16.79zm144.249.288C439.596 229.677 251.465 40.445 16.503 32.01 7.473 31.686 0 38.981 0 48.016v48.068c0 8.625 6.835 15.645 15.453 15.999 191.179 7.839 344.627 161.316 352.465 352.465.353 8.618 7.373 15.453 15.999 15.453h48.068c9.034-.001 16.329-7.474 16.005-16.504z"></path></svg>
            <span>Subscribe to RSS</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { buildShareLink } from '../../utils/share';
import { copyToClipboard, NOTHING_TO_COPY } from '../../utils/clipboard';
import { buildRssUrl, buildDownloadUrl } from '../../utils/api';
import { reportNotAllowedIfApplicable } from '../../core/faro';
import { createFocusTrap } from '../../shared-components/focusTrap';

export default {
  name: 'PlayerMenu',
  // D-237 (A4): `v-if`-mounted by `Player`, so mounted IS shown and `shown` is a
  // constant — unlike the two v3 overlays, which are `v-show`/class-toggled and are the
  // reason the trap keys on a predicate at all.
  mixins: [createFocusTrap({
    shown() { return true; },
    close() { this.close(); },
  })],
  emits: ['close', 'announce'],
  props: {
    podcast: Object,
    shows: [Array, Object],
    visible: Boolean,
    // Threaded from V4 Player so the RSS + download links honour `apiDomain`.
    // Without it both fell back to hardcoded prod hosts, so a uat/dev embed
    // offered the listener a PRODUCTION feed and download while playing
    // dev audio — see utils/api.js.
    apiPrefix: String,
  },
  data() {
    return {
      linkCopied: false,
    };
  },
  methods: {
    close() {
      this.$emit('close');
    },
    subscribeRSS() {
      // `shows` is the parsed body of postv4.../really_all.json, set in
      // app.vue. Tolerate both the array-at-top and { shows: [...] }
      // wrapper shapes; either has been seen in production traffic.
      const showsList = Array.isArray(this.shows)
        ? this.shows
        : (this.shows && this.shows.shows) || [];

      const show = showsList.find(s => s && s.id === this.podcast.showId);
      if (!show) return;

      const rssFeeds = (show.smcs || []).filter(s => s && s.type === 'rss');
      if (rssFeeds.length === 0) return;

      const feed = rssFeeds.find(f => f.title === show.title);
      const feedId = feed ? feed.id : rssFeeds[0].id;

      const rssLink = buildRssUrl(this.apiPrefix, this.podcast.station, feedId);
      window.open(rssLink, '_blank');
    },
    copyLink(event) {
      event.preventDefault();
      // #856: copyToClipboard prefers the async Clipboard API and falls back to
      // a synchronous execCommand copy when it is denied (Chrome cross-origin
      // iframe) or unavailable (http:// embed). It only rejects when BOTH fail.
      copyToClipboard(this.podcast.shortUrl)
        .then(() => {
          this.linkCopied = true;
          // D-235 (A3): the confirmation, on the channel that announces things, rather
          // than as a mutation of this control's own accessible name.
          this.$emit('announce', 'Link copied');
          setTimeout(() => {
            this.linkCopied = false;
          }, 2000);
        })
        .catch((error) => {
          // An episode with no shortUrl is OUR data problem, and telling the
          // listener to check their browser's clipboard settings sends them to
          // fix something that is not broken (Phase 2). Nothing was copied and
          // nothing claims otherwise — `linkCopied` stays false — so the control
          // simply does not confirm.
          if (error && error.code === NOTHING_TO_COPY) return;
          window.alert('Failed to copy to clipboard. Please check your browser\'s clipboard settings');
          // F-3/MR-69 review fold: this .catch() is exactly why a global
          // unhandledrejection listener in faro.js never fires — count it
          // here instead, at the actual call site. Reached only on a genuine
          // total failure (a fallback-recovered denial is not counted).
          reportNotAllowedIfApplicable(error, this.podcast && this.podcast.station);
        });
    },
    download() {
      const episodeRelativeLink = this.podcast.m4aFile;
      const link = buildDownloadUrl(this.apiPrefix, episodeRelativeLink);
      window.open(link, '_blank');
    },
    computedShareLink(type) {
      // Delegates to the tested share-URL builder — email/meta/x map to
      // mailto / facebook-sharer / twitter-intent (direct links, no
      // third-party forwarder). The template only ever calls email/meta/x.
      return buildShareLink(type, { url: this.podcast.shortUrl, title: this.podcast.name });
    },
  },
};

</script>


<style lang="scss" scoped>
// 🔴 D-233 (A2): REQUIRED, not tidiness. This component applies `.is-sr-only`, and a
// scoped style block does not inherit `_helpers.scss` from anywhere — so without this
// import the class has NO rules here and the label renders as VISIBLE TEXT. In
// `.close-button` (20x20, `display: flex`) that text also squeezed the sibling svg to
// width 0 and pushed it outside the button, i.e. the close X disappeared. MEASURED in a
// real chromium; no unit spec or visual cell could see it (see
// `test/unit/sr-only-class-is-defined.spec.js`, which now pins it at the source).
@import '../../assets/_helpers';

.vertical {
  .menu-content {
    display: flex;
    flex-direction: column;
    gap: 60px;
    padding: 50px 30px 30px 40px;
  }

}

.menu-wrap {
  position: absolute;
  top: 10px;
  left: 10px;
  right: 10px;
  bottom: 10px;
  border-radius: 6px;
  background-color: rgba(0, 0, 0, .8);
  backdrop-filter: blur(1rem);
  z-index: 9;
  font-family: 'Inter', sans-serif;
}

.close-button {
  position: absolute;
  top: 20px;
  right: 20px;
  height: 20px;
  width: 20px;
  cursor: pointer;
  z-index: 10;

  // D-233 (A2): this was a `<div>` wrapping an `<svg @click>` — mouse-only. Now a real
  // button, so the UA chrome has to come off in the SAME commit as the tag change or the
  // conversion is a look change. `display: flex` rather than the button default: the box
  // is exactly svg-sized (20x20), and a button's inline-block content box adds a
  // line-height gap under the glyph that would push it out of the square.
  background: transparent;
  border: 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  // D-242 (A2): 20x20 is under the 24x24 floor of WCAG 2.5.8 at EVERY width, not just on
  // a phone — so unlike R11's expanders this one is NOT `@media`-gated. Transparent and
  // out of flow, so the rendered box is unchanged; it sits in the overlay's corner with
  // no neighbouring target to steal from.
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 24px;
    height: 24px;
  }
}

.share-button {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: white;
  text-decoration: none;

  // D-233 (A2): three of the five controls carrying this class were an hrefless `<a>`
  // (Copy link) and two `<span>`s (Download, Subscribe to RSS) — none reachable from a
  // keyboard. They are `<button>`s now, and the other two are still real `<a href>`
  // share links, so this class dresses both. `font: inherit` is the load-bearing part:
  // a button does NOT inherit font-family or size, so without it the three converted
  // rows would render in the UA's 13.333px system font beside two that render in
  // `.menu-wrap`'s Inter. `text-align: left` undoes the button's centring, which the
  // 135px-wide Copy-link span would otherwise show.
  background: transparent;
  border: 0;
  padding: 0;
  font: inherit;
  text-align: left;

  &:hover {
    transition: all 0.2s ease-in-out;
    color: #90bdff;
  }
}

.menu-content {
  border-radius: 4px;
  padding: 30px 30px 30px 60px;
  color: white;
  display: flex;
  gap: 120px;

  .share-header {
    text-transform: uppercase;
    font-size: 12px;
    color: #7e7d7d;
    margin-bottom: 20px;
    letter-spacing: 2px;
  }

  .share-body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    color: white;
  }

  // R7 — mobile overlay. Measured before this block, at a 288px frame: the row
  // above puts 210px of chrome (120px gap + 90px padding) inside a 268px
  // overlay, so the second section ("Actions": Download / Subscribe to RSS)
  // landed at x=345..436 — entirely outside the frame, i.e. unreachable on a
  // phone — and `.menu-wrap` sets no overflow, so the spill escaped into the
  // document (body scrollWidth 436 against a 288px frame). Stack the sections
  // below the breakpoint and let the overlay scroll if it runs tall.
  // Paddings/gaps are tunable defaults; @media(max-width)-gated ONLY, so the
  // desktop two-column menu (and the frozen 1280px oracle) is untouched.
  //
  // ⚠️ EXCEPT the `vertical=true` variant, which this "mobile-only" framing does
  // NOT cover. The media query is evaluated against the IFRAME's viewport, and
  // the vertical variant is normally pinned to a 320px iframe whatever the host
  // width (contract register #3) — so (max-width: 480px) is true for it and
  // these rules ship to DESKTOP vertical embeds too. Measured on a 1280px host:
  // column=1248, iframe=320, mq480=true. Accepted deliberately (a 320px-wide
  // widget wants the mobile shape whatever the host width), and PARTIALLY locked by
  // `a wide host column still gets the full 320px vertical widget`
  // in test/e2e/responsive.spec.js — 🪤 which pins the 320px WIDTH only. Nothing
  // in the suite evaluates `(max-width: 480px)` or these mobile rules, so the look
  // change itself is unguarded. It is a look change on desktop and needs the same
  // sign-off as the mobile one.
  //
  // 🪤 "at EVERY host width" is what this said until 2026-09-06, and D-215 made
  // it false in ONE position. Register #3 is a LOADER bug: a param in FIRST
  // config position is dropped by `handleIframe`'s `new URLSearchParams(url)`
  // re-parse. While `vertical` was lowercase-only, a first-position
  // `vertical=True` was dropped by the loader AND ignored by the widget, so both
  // sides agreed on horizontal by accident. D-215 routed the widget's reader
  // through `pyBool`, so the widget now honours it and the loader still does
  // not: that one position renders the VERTICAL widget inside a 700-wide iframe
  // (host-page height 224 -> 705, MEASURED 2026-09-02). There, the iframe
  // viewport is 700 and (max-width: 480px) is FALSE — the variant gets the
  // desktop rules it was never designed for. Not fixed here; register #3's
  // fix-vs-preserve is deferred to the Vue3 embedder port. Do not restore the
  // absolute claim.
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 24px;
    padding: 50px 20px 20px;
    max-height: 100%;
    overflow-y: auto;
  }
}

</style>
